const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  ShoppingBag,
  Package,
  Users,
  Clock,
  Search,
  Filter,
  Plus,
  Minus,
  Save,
  RefreshCw,
  Edit3,
  Download,
  Building,
  Calendar,
  History,
  RotateCcw,
  X,
  Loader,
  FileText,
  FileSpreadsheet,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

const AdminDashboard = ({ onNavigate, user }) => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard stats from API
  const [dashboardStats, setDashboardStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Store product names for orders
  const [orderProductNames, setOrderProductNames] = useState({});
  const [loadingProductNames, setLoadingProductNames] = useState({});

  // Stock management
  const [editingStock, setEditingStock] = useState(null);
  const [stockValue, setStockValue] = useState(0);
  const [originalStock, setOriginalStock] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('current');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);

  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [statsHistory, setStatsHistory] = useState([]);
  const [resetHistory, setResetHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState('all');

  // Reset confirmation state
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetPeriodStart, setResetPeriodStart] = useState('');
  const [resetPeriodEnd, setResetPeriodEnd] = useState('');
  const [resetReason, setResetReason] = useState('');

  // API status
  const [apiStatus, setApiStatus] = useState({
    products: false,
    orders: false,
    members: false,
    dashboard: false,
  });

  const [diagnostics, setDiagnostics] = useState([]);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const addDiagnostic = (msg) => {
    setDiagnostics((prev) => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`].slice(-5));
  };

  // Helper functions
  const getCustomerName = (order) => {
    return order.customer_name || order.customerName || order.name || order.full_name || 'Guest';
  };

  const getOrderTotal = (order) => {
    return order.total_amount || order.totalAmount || order.total || 0;
  };

  const getOrderDate = (order) => {
    return order.order_date || order.orderDate || order.date || '';
  };

  // Fetch product names for a specific order from the orders API
  const fetchProductNamesForOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/get?id=${orderId}`, {
        headers: authHeaders(),
      });
      const data = await response.json();

      if (data.success && data.order) {
        const orderData = data.order;

        // Check items array
        if (orderData.items && Array.isArray(orderData.items) && orderData.items.length > 0) {
          return orderData.items.map((item) => item.name || item.product_name).join(', ');
        }
        // Check order_items array
        if (
          orderData.order_items &&
          Array.isArray(orderData.order_items) &&
          orderData.order_items.length > 0
        ) {
          return orderData.order_items.map((item) => item.product_name || item.name).join(', ');
        }
        // Single product name
        if (orderData.product_name) {
          return orderData.product_name;
        }
      }
      return `Order #${orderId}`;
    } catch (error) {
      return `Order #${orderId}`;
    }
  };

  // Load product names for recent orders
  const loadProductNamesForOrders = useCallback(
    async (recentOrdersList) => {
      if (!recentOrdersList || recentOrdersList.length === 0) return;

      for (const order of recentOrdersList) {
        const orderId = order.id;

        // Skip if already loaded or currently loading
        if (orderProductNames[orderId] || loadingProductNames[orderId]) continue;

        // Check if product names already exist in the order object
        if (order.product_names) {
          setOrderProductNames((prev) => ({ ...prev, [orderId]: order.product_names }));
          continue;
        }

        if (order.items && order.items.length > 0) {
          const names = order.items.map((item) => item.name || item.product_name).join(', ');
          setOrderProductNames((prev) => ({ ...prev, [orderId]: names }));
          continue;
        }

        if (order.order_items && order.order_items.length > 0) {
          const names = order.order_items.map((item) => item.product_name || item.name).join(', ');
          setOrderProductNames((prev) => ({ ...prev, [orderId]: names }));
          continue;
        }

        if (order.product_name) {
          setOrderProductNames((prev) => ({ ...prev, [orderId]: order.product_name }));
          continue;
        }

        // Fetch from API
        setLoadingProductNames((prev) => ({ ...prev, [orderId]: true }));

        const productNames = await fetchProductNamesForOrder(orderId);
        setOrderProductNames((prev) => ({ ...prev, [orderId]: productNames }));
        setLoadingProductNames((prev) => ({ ...prev, [orderId]: false }));
      }
    },
    [orderProductNames, loadingProductNames]
  );

  // Load product names when recent orders change
  useEffect(() => {
    if (
      _optionalChain([dashboardStats, 'optionalAccess', (_) => _.recent_orders]) &&
      dashboardStats.recent_orders.length > 0
    ) {
      loadProductNamesForOrders(dashboardStats.recent_orders);
    }
  }, [
    _optionalChain([dashboardStats, 'optionalAccess', (_2) => _2.recent_orders]),
    loadProductNamesForOrders,
  ]);

  // FIXED: Updated getDisplayStatus to handle all statuses properly
  const getDisplayStatus = (status) => {
    if (!status) return 'pending';

    const statusStr = String(status).toLowerCase();

    // Map all possible statuses
    if (statusStr === 'pending') return 'pending';
    if (statusStr === 'paid') return 'paid';
    if (statusStr === 'processing') return 'processing';
    if (statusStr === 'preparing') return 'preparing';
    if (statusStr === 'shipped') return 'shipped';
    if (statusStr === 'delivered') return 'delivered';
    if (statusStr === 'rejected') return 'rejected';
    if (statusStr === 'cancelled') return 'cancelled';
    if (statusStr === 'refunded') return 'refunded';
    if (statusStr === 'returned') return 'returned';
    if (statusStr === 'picked up') return 'picked_up';
    if (statusStr === 'ready for pickup') return 'ready_for_pickup';

    // If no match, return the actual status (don't default to pending)
    return statusStr;
  };

  // Get status badge color
  const getStatusBadgeClass = (status) => {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'delivered':
      case 'picked up':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'shipped':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'processing':
      case 'preparing':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'paid':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-stone-100 text-stone-500 border-stone-200';
      default:
        return 'bg-stone-100 text-stone-500 border-stone-200';
    }
  };

  // Fetch dashboard stats from API
  const fetchDashboardStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard?action=current&_=${Date.now()}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setDashboardStats(data.data);
        setApiStatus((prev) => ({ ...prev, dashboard: true }));
      } else {
        setApiStatus((prev) => ({ ...prev, dashboard: false }));
        calculateLocalStats();
      }
    } catch (error) {
      setApiStatus((prev) => ({ ...prev, dashboard: false }));
      calculateLocalStats();
    } finally {
      setStatsLoading(false);
    }
  }, [products, orders, members]);

  const calculateLocalStats = () => {
    const totalRevenue = orders.reduce(
      (sum, order) => sum + (parseFloat(order.total || order.total_amount) || 0),
      0
    );
    const totalOrders = orders.length;
    const totalProducts = products.length;
    const pendingOrders = orders.filter(
      (o) => o.status === 'Pending' || o.status === 'pending'
    ).length;
    const lowStockItems = products.filter((p) => (p.stock || 0) < 5).length;
    const totalMembers = members.length;

    setDashboardStats({
      total_revenue: totalRevenue,
      total_orders: totalOrders,
      total_products: totalProducts,
      pending_orders: pendingOrders,
      low_stock_items: lowStockItems,
      total_members: totalMembers,
      recent_orders: orders.slice(0, 5),
      low_stock_products: products.filter((p) => (p.stock || 0) < 5).slice(0, 10),
      timestamp: new Date().toISOString(),
    });
  };

  // Fetch products, orders, members
  const fetchProducts = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/products?admin=true&limit=100&_=${Date.now()}`,
        { headers: authHeaders() }
      );
      const data = await response.json();
      const extracted = extractData(data);
      setProducts(extracted);
      setApiStatus((prev) => ({ ...prev, products: true }));
      addDiagnostic(`Loaded ${extracted.length} products`);
    } catch (err) {
      addDiagnostic(`Error fetching products: ${err}`);
      setApiStatus((prev) => ({ ...prev, products: false }));
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/orders/get?admin=true&_=${Date.now()}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success && data.orders) {
        setOrders(data.orders);
        setApiStatus((prev) => ({ ...prev, orders: true }));
        addDiagnostic(`Loaded ${data.orders.length} orders`);
      } else {
        setApiStatus((prev) => ({ ...prev, orders: false }));
      }
    } catch (err) {
      addDiagnostic(`Error fetching orders: ${err}`);
      setApiStatus((prev) => ({ ...prev, orders: false }));
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/members?_=${Date.now()}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      const extracted = extractData(data);
      setMembers(extracted);
      setApiStatus((prev) => ({ ...prev, members: true }));
      addDiagnostic(`Loaded ${extracted.length} members`);
    } catch (err) {
      addDiagnostic(`Error fetching members: ${err}`);
      setApiStatus((prev) => ({ ...prev, members: false }));
    }
  };

  const extractData = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.members && Array.isArray(response.members)) return response.members;
    if (response.users && Array.isArray(response.users)) return response.users;
    if (response.products && Array.isArray(response.products)) return response.products;
    return [];
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    addDiagnostic('Starting dashboard data fetch...');

    await Promise.all([fetchProducts(), fetchOrders(), fetchMembers()]);

    await fetchDashboardStats();
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Capture daily stats automatically
  useEffect(() => {
    if (dashboardStats && !statsLoading) {
      const captureDailyStats = async () => {
        try {
          await fetch(`${API_BASE_URL}/dashboard?action=capture`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
            },
            body: JSON.stringify({ period_type: 'daily' }),
          });
        } catch (error) {}
      };
      captureDailyStats();
    }
  }, [dashboardStats, statsLoading]);

  // Fetch history data
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      let url = `${API_BASE_URL}/dashboard?action=history&limit=100`;
      if (historyPeriod !== 'all') {
        url += `&period=${historyPeriod}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setStatsHistory(
          _optionalChain([
            data,
            'access',
            (_3) => _3.data,
            'optionalAccess',
            (_4) => _4.stats_history,
          ]) || []
        );
        setResetHistory(
          _optionalChain([
            data,
            'access',
            (_5) => _5.data,
            'optionalAccess',
            (_6) => _6.reset_history,
          ]) || []
        );
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };

  // Reset dashboard
  const handleResetDashboard = async () => {
    if (!resetPeriodStart) {
      showToast('Please select the period start date for this reset.', 'error');
      return;
    }

    setResetting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard?action=reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({
          reset_by: _optionalChain([user, 'optionalAccess', (_7) => _7.name]) || 'admin',
          period_start: resetPeriodStart,
          period_end: resetPeriodEnd || new Date().toISOString().split('T')[0],
          reason: resetReason || 'Manual reset',
        }),
      });

      const data = await response.json();
      if (data.success) {
        showToast('Dashboard reset successfully! Stats have been archived.');
        setShowResetConfirm(false);
        setResetPeriodStart('');
        setResetPeriodEnd('');
        setResetReason('');
        await fetchDashboardStats();
      } else {
        showToast('Failed to reset dashboard: ' + (data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast('Failed to reset dashboard. Please try again.', 'error');
    } finally {
      setResetting(false);
    }
  };

  // Export data
  const handleExport = async () => {
    setExporting(true);

    let url = `${API_BASE_URL}/dashboard?action=export&format=${exportFormat}&_=${Date.now()}`;

    if (exportPeriod === 'custom' && exportStartDate && exportEndDate) {
      url += `&period=custom&start_date=${exportStartDate}&end_date=${exportEndDate}`;
    } else {
      url += `&period=current`;
    }

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      let filename = `dashboard_export_${new Date().toISOString().split('T')[0]}`;
      if (exportPeriod === 'custom' && exportStartDate && exportEndDate) {
        filename = `dashboard_export_${exportStartDate}_to_${exportEndDate}`;
      }
      filename += exportFormat === 'csv' ? '.csv' : '.json';

      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(urlObj);

      setShowExportModal(false);
    } catch (error) {
      showToast('Failed to export data. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Stock management
  const handleStockEdit = (productId, currentStock = 0) => {
    setEditingStock(productId);
    setStockValue(currentStock);
    setOriginalStock(currentStock);
  };

  const handleStockChange = (delta) => {
    setStockValue((prev) => Math.max(0, prev + delta));
  };

  const handleStockSave = async (productId) => {
    try {
      const quantityDiff = stockValue - originalStock;
      if (quantityDiff === 0) {
        setEditingStock(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/products/update-stock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: Math.abs(quantityDiff),
          operation: quantityDiff > 0 ? 'increase' : 'decrease',
        }),
      });

      const data = await response.json();
      if (data.success) {
        setProducts((prev) =>
          prev.map((p) => (p.id === productId ? { ...p, stock: stockValue } : p))
        );
        setEditingStock(null);
        await fetchDashboardStats();
      } else {
        showToast('Failed to update stock: ' + (data.error || 'Unknown error'), 'error');
      }
    } catch (error) {
      showToast('Network error while updating stock.', 'error');
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(isNaN(num) ? 0 : num);
  };

  const getStockStatus = (stock = 0) => {
    if (stock === 0)
      return { status: 'Out of Stock', color: 'bg-red-500', textColor: 'text-red-600' };
    if (stock < 5)
      return { status: 'Low Stock', color: 'bg-orange-500', textColor: 'text-orange-600' };
    return { status: 'In Stock', color: 'bg-green-500', textColor: 'text-green-600' };
  };

  const categories = ['all', ...new Set(products.map((p) => p.category).filter(Boolean))];
  const filteredProducts = products.filter((product) => {
    const matchesSearch = _optionalChain([
      product,
      'access',
      (_8) => _8.name,
      'optionalAccess',
      (_9) => _9.toLowerCase,
      'call',
      (_10) => _10(),
      'access',
      (_11) => _11.includes,
      'call',
      (_12) => _12(searchTerm.toLowerCase()),
    ]);
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const recentOrders =
    _optionalChain([
      dashboardStats,
      'optionalAccess',
      (_13) => _13.recent_orders,
      'optionalAccess',
      (_14) => _14.slice,
      'call',
      (_15) => _15(0, 5),
    ]) || [];

  if (loading) {
    return React.createElement(
      'div',
      {
        className: 'pt-44 pb-12 min-h-screen bg-stone-50',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 562 },
      },
      React.createElement(
        'div',
        {
          className: 'max-w-7xl mx-auto px-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 563 },
        },
        React.createElement(
          'div',
          {
            className: 'flex justify-center items-center h-64',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 564 },
          },
          React.createElement(
            'div',
            {
              className: 'text-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 565 },
            },
            React.createElement(Loader, {
              size: 40,
              className: 'animate-spin text-gold-600 mx-auto mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 566 },
            }),
            React.createElement(
              'p',
              {
                className: 'text-stone-500',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 567 },
              },
              'Loading dashboard data...'
            ),
            React.createElement(
              'p',
              {
                className: 'text-xs text-stone-400 mt-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 568 },
              },
              !apiStatus.products && 'Loading products... ',
              !apiStatus.orders && 'Loading orders... ',
              !apiStatus.members && 'Loading members... '
            )
          )
        )
      )
    );
  }

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 581 },
    },
    toast &&
      React.createElement(
        'div',
        {
          className: `fixed top-6 right-6 z-[999] flex items-center gap-3 px-5 py-4 rounded-sm shadow-xl text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-stone-900 text-white'}`,
          style: { minWidth: '280px' },
        },
        React.createElement('span', null, toast.message),
        React.createElement(
          'button',
          {
            onClick: () => setToast(null),
            className: 'ml-auto text-white/70 hover:text-white text-lg leading-none',
          },
          '×'
        )
      ),
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 582 },
      },

      /* Header */
      React.createElement(
        'div',
        {
          className:
            'flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 585 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 586 } },
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 587 },
            },
            'Maison Dashboard'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-500 text-sm font-light mt-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 588 },
            },
            'Welcome back, ',
            _optionalChain([user, 'optionalAccess', (_16) => _16.name]) || 'Administrator',
            '. System is ready for new transactions.'
          ),
          React.createElement(
            'div',
            {
              className: 'flex gap-2 mt-2 text-xs text-stone-400',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 591 },
            },
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 592 } },
              'Products: ',
              _optionalChain([dashboardStats, 'optionalAccess', (_17) => _17.total_products]) || 0
            ),
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 593 } },
              'Orders: ',
              _optionalChain([dashboardStats, 'optionalAccess', (_18) => _18.total_orders]) || 0
            ),
            React.createElement(
              'span',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 594 } },
              'Members: ',
              _optionalChain([dashboardStats, 'optionalAccess', (_19) => _19.total_members]) || 0
            ),
            _optionalChain([dashboardStats, 'optionalAccess', (_20) => _20.timestamp]) &&
              React.createElement(
                'span',
                { __self: this, __source: { fileName: _jsxFileName, lineNumber: 596 } },
                'Last updated: ',
                new Date(dashboardStats.timestamp).toLocaleTimeString()
              )
          )
        ),
        React.createElement(
          'div',
          {
            className: 'flex gap-3 flex-wrap',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 600 },
          },
          React.createElement(
            'button',
            {
              onClick: () => setShowHistoryModal(true),
              className:
                'px-4 py-2 bg-white border border-stone-200 text-stone-600 text-xs uppercase tracking-wider hover:bg-stone-50 transition-colors flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 601 },
            },

            React.createElement(History, {
              size: 14,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 605 },
            }),
            ' History'
          ),
          React.createElement(
            'button',
            {
              onClick: () => setShowExportModal(true),
              className:
                'px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-wider hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 607 },
            },

            React.createElement(Download, {
              size: 14,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 611 },
            }),
            ' Export'
          ),
          React.createElement(
            'button',
            {
              onClick: () => setShowResetConfirm(true),
              className:
                'px-4 py-2 border border-orange-300 text-orange-600 text-xs uppercase tracking-wider hover:bg-orange-50 transition-colors flex items-center gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 613 },
            },

            React.createElement(RotateCcw, {
              size: 14,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 617 },
            }),
            ' Reset'
          ),
          React.createElement(
            'button',
            {
              onClick: fetchAllData,
              disabled: loading,
              className:
                'px-4 py-2 bg-white border border-stone-200 text-stone-600 text-xs uppercase tracking-wider hover:bg-stone-50 transition-colors flex items-center gap-2 disabled:opacity-50',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 619 },
            },

            React.createElement(RefreshCw, {
              size: 14,
              className: loading ? 'animate-spin' : '',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 624 },
            }),
            'Refresh'
          )
        )
      ),

      /* Stats Grid */
      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 631 },
        },
        React.createElement(
          'div',
          {
            className:
              'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 632 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between items-start mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 633 },
            },
            React.createElement(
              'div',
              {
                className:
                  'p-3 rounded-full border bg-emerald-50 text-emerald-600 border-emerald-100',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 634 },
              },
              React.createElement(DollarSign, {
                size: 22,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 635 },
              })
            ),
            React.createElement(
              'span',
              {
                className: 'text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 637 },
              },
              _optionalChain([dashboardStats, 'optionalAccess', (_21) => _21.total_revenue])
                ? '+100%'
                : '0%'
            )
          ),
          React.createElement(
            'h3',
            {
              className: 'text-stone-500 text-xs uppercase tracking-widest font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 641 },
            },
            'Total Revenue'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-2xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 642 },
            },
            formatCurrency(
              _optionalChain([dashboardStats, 'optionalAccess', (_22) => _22.total_revenue]) || 0
            )
          )
        ),

        React.createElement(
          'div',
          {
            className:
              'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 645 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between items-start mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 646 },
            },
            React.createElement(
              'div',
              {
                className: 'p-3 rounded-full border bg-blue-50 text-blue-600 border-blue-100',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 647 },
              },
              React.createElement(ShoppingBag, {
                size: 22,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 648 },
              })
            ),
            React.createElement(
              'span',
              {
                className: 'text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 650 },
              },
              _optionalChain([dashboardStats, 'optionalAccess', (_23) => _23.total_orders])
                ? '+100%'
                : '0%'
            )
          ),
          React.createElement(
            'h3',
            {
              className: 'text-stone-500 text-xs uppercase tracking-widest font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 654 },
            },
            'Total Orders'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-2xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 655 },
            },
            _optionalChain([dashboardStats, 'optionalAccess', (_24) => _24.total_orders]) || 0
          )
        ),

        React.createElement(
          'div',
          {
            className:
              'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 658 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between items-start mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 659 },
            },
            React.createElement(
              'div',
              {
                className: 'p-3 rounded-full border bg-gold-50 text-gold-600 border-gold-100',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 660 },
              },
              React.createElement(Package, {
                size: 22,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 661 },
              })
            ),
            React.createElement(
              'span',
              {
                className: 'text-xs font-bold px-2 py-1 rounded-full bg-orange-50 text-orange-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 663 },
              },
              _optionalChain([dashboardStats, 'optionalAccess', (_25) => _25.low_stock_items]) || 0,
              ' low stock'
            )
          ),
          React.createElement(
            'h3',
            {
              className: 'text-stone-500 text-xs uppercase tracking-widest font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 667 },
            },
            'Products in Vault'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-2xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 668 },
            },
            _optionalChain([dashboardStats, 'optionalAccess', (_26) => _26.total_products]) || 0
          )
        ),

        React.createElement(
          'div',
          {
            className:
              'bg-white p-6 border border-stone-200 shadow-sm rounded-sm hover:shadow-md transition-shadow',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 671 },
          },
          React.createElement(
            'div',
            {
              className: 'flex justify-between items-start mb-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 672 },
            },
            React.createElement(
              'div',
              {
                className: 'p-3 rounded-full border bg-orange-50 text-orange-600 border-orange-100',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 673 },
              },
              React.createElement(Clock, {
                size: 22,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 674 },
              })
            ),
            React.createElement(
              'span',
              {
                className: 'text-xs font-bold px-2 py-1 rounded-full bg-green-50 text-green-700',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 676 },
              },
              _optionalChain([dashboardStats, 'optionalAccess', (_27) => _27.pending_orders]) === 0
                ? 'None'
                : `${_optionalChain([dashboardStats, 'optionalAccess', (_28) => _28.pending_orders])} awaiting`
            )
          ),
          React.createElement(
            'h3',
            {
              className: 'text-stone-500 text-xs uppercase tracking-widest font-bold mb-1',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 680 },
            },
            'Pending Orders'
          ),
          React.createElement(
            'p',
            {
              className: 'font-serif text-2xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 681 },
            },
            _optionalChain([dashboardStats, 'optionalAccess', (_29) => _29.pending_orders]) || 0
          )
        )
      ),

      React.createElement(
        'div',
        {
          className: 'grid grid-cols-1 lg:grid-cols-3 gap-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 685 },
        },

        /* Recent Orders - visible to anyone who can access the dashboard (data comes from dashboard stats) */
        React.createElement(
          'div',
          {
            className:
              'lg:col-span-2 bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 688 },
          },
          React.createElement(
            'div',
            {
              className: 'p-6 border-b border-stone-100 flex justify-between items-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 689 },
            },
            React.createElement(
              'h3',
              {
                className: 'font-serif text-xl text-stone-900',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 690 },
              },
              'Recent Orders'
            ),
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-transactions'),
                className:
                  'text-xs text-gold-600 hover:text-gold-500 uppercase tracking-widest font-bold',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 691 },
              },
              'View All (',
              _optionalChain([dashboardStats, 'optionalAccess', (_30) => _30.total_orders]) || 0,
              ')'
            )
          ),
          React.createElement(
            'div',
            {
              className: 'overflow-x-auto',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 698 },
            },
            React.createElement(
              'table',
              {
                className: 'w-full text-left',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 699 },
              },
              React.createElement(
                'thead',
                {
                  className:
                    'bg-stone-50 text-stone-500 text-[10px] uppercase tracking-widest font-bold',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 700 },
                },
                React.createElement(
                  'tr',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 701 } },
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 702 },
                    },
                    'Product Name'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 703 },
                    },
                    'Customer'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 704 },
                    },
                    'Total'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 705 },
                    },
                    'Status'
                  ),
                  React.createElement(
                    'th',
                    {
                      className: 'px-6 py-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 706 },
                    },
                    'Date'
                  )
                )
              ),
              React.createElement(
                'tbody',
                {
                  className: 'divide-y divide-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 709 },
                },
                recentOrders.length > 0
                  ? recentOrders.map((order) => {
                      const customerName = getCustomerName(order);
                      const initial = customerName.charAt(0).toUpperCase();
                      const total = getOrderTotal(order);
                      const actualStatus = order.status || 'pending';
                      const statusBadgeClass = getStatusBadgeClass(actualStatus);
                      const displayStatus = getDisplayStatus(actualStatus);

                      // Get product name from state or show loading/fallback
                      let productNames = orderProductNames[order.id];
                      const isLoading = loadingProductNames[order.id];

                      if (!productNames && !isLoading) {
                        productNames = `Order #${order.order_number || order.id}`;
                      }

                      // Truncate long product names
                      const truncatedProductNames =
                        productNames && productNames.length > 50
                          ? productNames.substring(0, 47) + '...'
                          : productNames;

                      return React.createElement(
                        'tr',
                        {
                          key: order.id,
                          className: 'hover:bg-stone-50/50 transition-colors',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 733 },
                        },
                        React.createElement(
                          'td',
                          {
                            className: 'px-6 py-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 734 },
                          },
                          React.createElement(
                            'div',
                            {
                              className:
                                'text-sm font-medium text-stone-900 max-w-[250px] truncate',
                              title: productNames,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 735 },
                            },
                            isLoading
                              ? React.createElement(
                                  'span',
                                  {
                                    className: 'flex items-center gap-2',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 737 },
                                  },
                                  React.createElement(Loader, {
                                    size: 12,
                                    className: 'animate-spin text-gold-600',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 738 },
                                  }),
                                  React.createElement(
                                    'span',
                                    {
                                      className: 'text-stone-400',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 739 },
                                    },
                                    'Loading...'
                                  )
                                )
                              : truncatedProductNames
                          )
                        ),
                        React.createElement(
                          'td',
                          {
                            className: 'px-6 py-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 746 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: 'flex items-center gap-3',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 747 },
                            },
                            React.createElement(
                              'div',
                              {
                                className:
                                  'w-6 h-6 bg-stone-200 rounded-full flex items-center justify-center text-[10px] font-bold',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 748 },
                              },
                              initial
                            ),
                            React.createElement(
                              'span',
                              {
                                className: 'text-sm text-stone-700',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 751 },
                              },
                              customerName
                            )
                          )
                        ),
                        React.createElement(
                          'td',
                          {
                            className: 'px-6 py-4 text-sm font-serif text-stone-900',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 754 },
                          },
                          formatCurrency(total)
                        ),
                        React.createElement(
                          'td',
                          {
                            className: 'px-6 py-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 757 },
                          },
                          React.createElement(
                            'span',
                            {
                              className: `inline-block px-2 py-1 rounded-full text-[10px] uppercase font-bold tracking-wide ${statusBadgeClass}`,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 758 },
                            },
                            displayStatus
                          )
                        ),
                        React.createElement(
                          'td',
                          {
                            className: 'px-6 py-4 text-xs text-stone-500',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 762 },
                          },
                          getOrderDate(order).split(' ')[0]
                        )
                      );
                    })
                  : React.createElement(
                      'tr',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 769 } },
                      React.createElement(
                        'td',
                        {
                          colSpan: 5,
                          className: 'px-6 py-8 text-center text-sm text-stone-400 italic',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 770 },
                        },
                        'No orders found.'
                      )
                    )
              )
            )
          )
        ),

        /* Stock Levels - only visible with view_products or manage_products permission */
        (user?.role === 'admin' ||
          (user?.permissions || []).includes('view_products') ||
          (user?.permissions || []).includes('manage_products')) &&
          React.createElement(
            'div',
            {
              className: 'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 781 },
            },
            React.createElement(
              'div',
              {
                className: 'p-6 border-b border-stone-100 flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 782 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 783 },
                },
                'Stock Levels'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => onNavigate('admin-products'),
                  className:
                    'text-xs text-gold-600 hover:text-gold-500 uppercase tracking-widest font-bold',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 784 },
                },
                'Manage (',
                _optionalChain([dashboardStats, 'optionalAccess', (_31) => _31.total_products]) ||
                  0,
                ')'
              )
            ),

            React.createElement(
              'div',
              {
                className: 'p-4 border-b border-stone-100 bg-stone-50/30',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 792 },
              },
              React.createElement(
                'div',
                {
                  className: 'relative mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 793 },
                },
                React.createElement(Search, {
                  className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 794 },
                }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Search products...',
                  value: searchTerm,
                  onChange: (e) => setSearchTerm(e.target.value),
                  className:
                    'w-full pl-9 pr-4 py-2 border border-stone-200 rounded-sm text-sm focus:border-gold-500 focus:outline-none',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 795 },
                })
              ),
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 803 },
                },
                React.createElement(Filter, {
                  size: 14,
                  className: 'text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 804 },
                }),
                React.createElement(
                  'select',
                  {
                    value: filterCategory,
                    onChange: (e) => setFilterCategory(e.target.value),
                    className:
                      'border border-stone-200 rounded-sm px-2 py-1 text-xs focus:border-gold-500 focus:outline-none',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 805 },
                  },

                  categories.map((cat) =>
                    React.createElement(
                      'option',
                      {
                        key: cat,
                        value: cat,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 811 },
                      },
                      cat === 'all' ? 'All Categories' : cat
                    )
                  )
                )
              )
            ),

            React.createElement(
              'div',
              {
                className: 'p-0 max-h-96 overflow-y-auto',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 819 },
              },
              filteredProducts.length > 0
                ? filteredProducts.map((item) => {
                    const stockStatus = getStockStatus(item.stock);
                    return React.createElement(
                      'div',
                      {
                        key: item.id,
                        className:
                          'p-4 border-b border-stone-50 hover:bg-stone-50 transition-colors',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 824 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-center justify-between mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 825 },
                        },
                        React.createElement(
                          'div',
                          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 826 } },
                          React.createElement(
                            'p',
                            {
                              className: 'text-sm font-medium text-stone-900',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 827 },
                            },
                            item.name
                          ),
                          React.createElement(
                            'p',
                            {
                              className: 'text-xs text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 828 },
                            },
                            item.category
                          )
                        ),
                        React.createElement(
                          'div',
                          {
                            className: 'text-right',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 830 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: 'flex items-center justify-end gap-2 mb-1',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 831 },
                            },
                            React.createElement('span', {
                              className: `w-2 h-2 rounded-full ${stockStatus.color}`,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 832 },
                            }),
                            React.createElement(
                              'span',
                              {
                                className: `text-[10px] uppercase font-bold ${stockStatus.textColor}`,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 833 },
                              },
                              stockStatus.status
                            )
                          ),
                          React.createElement(
                            'p',
                            {
                              className: 'text-xs text-stone-400',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 837 },
                            },
                            item.stock || 0,
                            ' Units'
                          )
                        )
                      ),

                      editingStock === item.id
                        ? React.createElement(
                            'div',
                            {
                              className: 'flex items-center gap-2 mt-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 842 },
                            },
                            React.createElement(
                              'button',
                              {
                                onClick: () => handleStockChange(-1),
                                className: 'p-1 border border-stone-200 rounded hover:bg-stone-50',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 843 },
                              },
                              React.createElement(Minus, {
                                size: 12,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 844 },
                              })
                            ),
                            React.createElement('input', {
                              type: 'number',
                              value: stockValue,
                              onChange: (e) => setStockValue(parseInt(e.target.value) || 0),
                              className:
                                'w-16 text-center border border-stone-200 rounded-sm py-1 text-sm',
                              min: '0',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 846 },
                            }),
                            React.createElement(
                              'button',
                              {
                                onClick: () => handleStockChange(1),
                                className: 'p-1 border border-stone-200 rounded hover:bg-stone-50',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 853 },
                              },
                              React.createElement(Plus, {
                                size: 12,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 854 },
                              })
                            ),
                            React.createElement(
                              'button',
                              {
                                onClick: () => handleStockSave(item.id),
                                className:
                                  'ml-auto bg-stone-900 text-white px-3 py-1 text-[10px] uppercase tracking-wider rounded-sm hover:bg-gold-600 transition-colors flex items-center gap-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 856 },
                              },
                              React.createElement(Save, {
                                size: 10,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 857 },
                              }),
                              ' Save'
                            )
                          )
                        : React.createElement(
                            'div',
                            {
                              className: 'flex justify-between items-center mt-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 861 },
                            },
                            React.createElement(
                              'div',
                              {
                                className: 'w-full bg-stone-100 h-1.5 rounded-full overflow-hidden',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 862 },
                              },
                              React.createElement('div', {
                                className: `h-full ${(item.stock || 0) === 0 ? 'bg-red-500' : (item.stock || 0) < 5 ? 'bg-orange-500' : 'bg-gold-600'}`,
                                style: {
                                  width: `${Math.min(100, ((item.stock || 0) / 20) * 100)}%`,
                                },
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 863 },
                              })
                            ),
                            (user?.role === 'admin' ||
                              (user?.permissions || []).includes('manage_stock')) &&
                              React.createElement(
                                'button',
                                {
                                  onClick: () => handleStockEdit(item.id, item.stock),
                                  className: 'ml-3 text-stone-400 hover:text-stone-900',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 868 },
                                },
                                React.createElement(Edit3, {
                                  size: 12,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 869 },
                                })
                              )
                          )
                    );
                  })
                : React.createElement(
                    'div',
                    {
                      className: 'p-8 text-center text-stone-400 text-sm italic',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 877 },
                    },
                    products.length === 0 ? 'Inventory is empty.' : 'No products match your search.'
                  )
            )
          )
      ),

      /* Quick Actions Row — only show buttons user has permission for */
      React.createElement(
        'div',
        { className: 'mt-8 grid grid-cols-1 md:grid-cols-4 gap-4' },
        (user?.role === 'admin' || (user?.permissions || []).includes('manage_products')) &&
          React.createElement(
            'button',
            {
              onClick: () => onNavigate('admin-add-product'),
              className:
                'bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-3' },
              React.createElement(
                'div',
                {
                  className:
                    'bg-gold-50 p-2 rounded-full group-hover:bg-gold-600 transition-colors',
                },
                React.createElement(Plus, {
                  size: 16,
                  className: 'text-gold-600 group-hover:text-white',
                })
              ),
              React.createElement(
                'div',
                null,
                React.createElement(
                  'h3',
                  { className: 'font-bold text-stone-900 text-sm' },
                  'Add Product'
                ),
                React.createElement(
                  'p',
                  { className: 'text-xs text-stone-500' },
                  'Add new items to inventory'
                )
              )
            )
          ),

        (user?.role === 'admin' || (user?.permissions || []).includes('view_orders')) &&
          React.createElement(
            'button',
            {
              onClick: () => onNavigate('admin-transactions'),
              className:
                'bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-3' },
              React.createElement(
                'div',
                {
                  className:
                    'bg-blue-50 p-2 rounded-full group-hover:bg-blue-600 transition-colors',
                },
                React.createElement(ShoppingBag, {
                  size: 16,
                  className: 'text-blue-600 group-hover:text-white',
                })
              ),
              React.createElement(
                'div',
                null,
                React.createElement(
                  'h3',
                  { className: 'font-bold text-stone-900 text-sm' },
                  'View Orders'
                ),
                React.createElement(
                  'p',
                  { className: 'text-xs text-stone-500' },
                  _optionalChain([dashboardStats, 'optionalAccess', (_32) => _32.pending_orders]) ||
                    0,
                  ' pending approval'
                )
              )
            )
          ),

        (user?.role === 'admin' || (user?.permissions || []).includes('view_members')) &&
          React.createElement(
            'button',
            {
              onClick: () => onNavigate('admin-members'),
              className:
                'bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-3' },
              React.createElement(
                'div',
                {
                  className:
                    'bg-emerald-50 p-2 rounded-full group-hover:bg-emerald-600 transition-colors',
                },
                React.createElement(Users, {
                  size: 16,
                  className: 'text-emerald-600 group-hover:text-white',
                })
              ),
              React.createElement(
                'div',
                null,
                React.createElement(
                  'h3',
                  { className: 'font-bold text-stone-900 text-sm' },
                  'Members'
                ),
                React.createElement(
                  'p',
                  { className: 'text-xs text-stone-500' },
                  _optionalChain([dashboardStats, 'optionalAccess', (_33) => _33.total_members]) ||
                    0,
                  ' registered'
                )
              )
            )
          ),

        (user?.role === 'admin' || (user?.permissions || []).includes('manage_bank_details')) &&
          React.createElement(
            'button',
            {
              onClick: () => onNavigate('admin-bank-details'),
              className:
                'bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-3' },
              React.createElement(
                'div',
                {
                  className:
                    'bg-purple-50 p-2 rounded-full group-hover:bg-purple-600 transition-colors',
                },
                React.createElement(Building, {
                  size: 16,
                  className: 'text-purple-600 group-hover:text-white',
                })
              ),
              React.createElement(
                'div',
                null,
                React.createElement(
                  'h3',
                  { className: 'font-bold text-stone-900 text-sm' },
                  'Bank Accounts'
                ),
                React.createElement(
                  'p',
                  { className: 'text-xs text-stone-500' },
                  'Manage transfer details'
                )
              )
            )
          ),

        (user?.role === 'admin' || (user?.permissions || []).includes('manage_staff')) &&
          React.createElement(
            'button',
            {
              onClick: () => onNavigate('admin-staff'),
              className:
                'bg-white border border-stone-200 p-4 rounded-sm hover:shadow-md transition-all text-left group',
            },
            React.createElement(
              'div',
              { className: 'flex items-center gap-3' },
              React.createElement(
                'div',
                {
                  className:
                    'bg-rose-50 p-2 rounded-full group-hover:bg-rose-600 transition-colors',
                },
                React.createElement(
                  'svg',
                  {
                    xmlns: 'http://www.w3.org/2000/svg',
                    width: 16,
                    height: 16,
                    viewBox: '0 0 24 24',
                    fill: 'none',
                    stroke: 'currentColor',
                    strokeWidth: 2,
                    className: 'text-rose-600 group-hover:text-white',
                  },
                  React.createElement('path', { d: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' }),
                  React.createElement('circle', { cx: 9, cy: 7, r: 4 }),
                  React.createElement('path', { d: 'M23 21v-2a4 4 0 0 0-3-3.87' }),
                  React.createElement('path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' })
                )
              ),
              React.createElement(
                'div',
                null,
                React.createElement(
                  'h3',
                  { className: 'font-bold text-stone-900 text-sm' },
                  'Staff Management'
                ),
                React.createElement(
                  'p',
                  { className: 'text-xs text-stone-500' },
                  'Roles & permissions'
                )
              )
            )
          )
      )
    ),

    /* Export Modal */
    showExportModal &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 939 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/60 backdrop-blur-sm',
          onClick: () => setShowExportModal(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 940 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 941 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 942 },
            },
            React.createElement(
              'div',
              {
                className: 'flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 943 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 944 },
                },
                'Export Dashboard Data'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => setShowExportModal(false),
                  className: 'text-stone-400 hover:text-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 945 },
                },
                React.createElement(X, {
                  size: 20,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 946 },
                })
              )
            )
          ),

          React.createElement(
            'div',
            {
              className: 'p-6 space-y-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 951 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 952 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 953 },
                },
                'Export Period'
              ),
              React.createElement(
                'div',
                {
                  className: 'flex gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 954 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'flex items-center gap-2 cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 955 },
                  },
                  React.createElement('input', {
                    type: 'radio',
                    checked: exportPeriod === 'current',
                    onChange: () => setExportPeriod('current'),
                    className: 'accent-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 956 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 957 },
                    },
                    'Current Stats'
                  )
                ),
                React.createElement(
                  'label',
                  {
                    className: 'flex items-center gap-2 cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 959 },
                  },
                  React.createElement('input', {
                    type: 'radio',
                    checked: exportPeriod === 'custom',
                    onChange: () => setExportPeriod('custom'),
                    className: 'accent-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 960 },
                  }),
                  React.createElement(
                    'span',
                    {
                      className: 'text-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 961 },
                    },
                    'Custom Range'
                  )
                )
              )
            ),

            exportPeriod === 'custom' &&
              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-2 gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 967 },
                },
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 968 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 969 },
                    },
                    'Start Date'
                  ),
                  React.createElement('input', {
                    type: 'date',
                    value: exportStartDate,
                    onChange: (e) => setExportStartDate(e.target.value),
                    className: 'w-full border border-stone-300 p-2 rounded-sm text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 970 },
                  })
                ),
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 972 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 973 },
                    },
                    'End Date'
                  ),
                  React.createElement('input', {
                    type: 'date',
                    value: exportEndDate,
                    onChange: (e) => setExportEndDate(e.target.value),
                    className: 'w-full border border-stone-300 p-2 rounded-sm text-sm',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 974 },
                  })
                )
              ),

            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 979 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 980 },
                },
                'Export Format'
              ),
              React.createElement(
                'div',
                {
                  className: 'flex gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 981 },
                },
                React.createElement(
                  'label',
                  {
                    className: 'flex items-center gap-2 cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 982 },
                  },
                  React.createElement('input', {
                    type: 'radio',
                    checked: exportFormat === 'csv',
                    onChange: () => setExportFormat('csv'),
                    className: 'accent-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 983 },
                  }),
                  React.createElement(FileText, {
                    size: 16,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 984 },
                  }),
                  ' CSV'
                ),
                React.createElement(
                  'label',
                  {
                    className: 'flex items-center gap-2 cursor-pointer',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 986 },
                  },
                  React.createElement('input', {
                    type: 'radio',
                    checked: exportFormat === 'json',
                    onChange: () => setExportFormat('json'),
                    className: 'accent-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 987 },
                  }),
                  React.createElement(FileSpreadsheet, {
                    size: 16,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 988 },
                  }),
                  ' JSON'
                )
              )
            ),

            React.createElement(
              'button',
              {
                onClick: handleExport,
                disabled: exporting,
                className:
                  'w-full bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 993 },
              },
              exporting
                ? React.createElement(Loader, {
                    size: 16,
                    className: 'animate-spin',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 994 },
                  })
                : React.createElement(Download, {
                    size: 16,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 994 },
                  }),
              exporting ? 'Exporting...' : 'Export Data'
            )
          )
        )
      ),

    /* Reset Confirmation Modal */
    showResetConfirm &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1004 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/60 backdrop-blur-sm',
          onClick: () => setShowResetConfirm(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1005 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-md rounded-sm shadow-2xl mx-4 overflow-hidden',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1006 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-orange-600 text-white p-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1007 },
            },
            React.createElement(
              'div',
              {
                className: 'flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1008 },
              },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1009 },
                },
                'Reset Dashboard'
              ),
              React.createElement(
                'button',
                {
                  onClick: () => setShowResetConfirm(false),
                  className: 'text-white/80 hover:text-white',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1010 },
                },
                React.createElement(X, {
                  size: 20,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1011 },
                })
              )
            ),
            React.createElement(
              'p',
              {
                className: 'text-xs text-white/80 mt-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1014 },
              },
              'This will archive current data and reset counters. All data will still be viewable in history.'
            )
          ),

          React.createElement(
            'div',
            {
              className: 'p-6 space-y-4',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1017 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1018 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1019 },
                },
                'Period Start Date *'
              ),
              React.createElement('input', {
                type: 'date',
                value: resetPeriodStart,
                onChange: (e) => setResetPeriodStart(e.target.value),
                className: 'w-full border border-stone-300 p-2 rounded-sm text-sm',
                required: true,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1020 },
              })
            ),
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1022 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1023 },
                },
                'Period End Date'
              ),
              React.createElement('input', {
                type: 'date',
                value: resetPeriodEnd,
                onChange: (e) => setResetPeriodEnd(e.target.value),
                className: 'w-full border border-stone-300 p-2 rounded-sm text-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1024 },
              })
            ),
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1026 } },
              React.createElement(
                'label',
                {
                  className:
                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1027 },
                },
                'Reason (Optional)'
              ),
              React.createElement('input', {
                type: 'text',
                value: resetReason,
                onChange: (e) => setResetReason(e.target.value),
                placeholder: 'e.g., Monthly reset, Quarter end, etc.',
                className: 'w-full border border-stone-300 p-2 rounded-sm text-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1028 },
              })
            ),

            React.createElement(
              'div',
              {
                className: 'flex gap-3 pt-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1031 },
              },
              React.createElement(
                'button',
                {
                  onClick: () => setShowResetConfirm(false),
                  className:
                    'flex-1 border border-stone-300 text-stone-600 py-2 text-xs uppercase tracking-widest hover:bg-stone-50',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1032 },
                },
                'Cancel'
              ),
              React.createElement(
                'button',
                {
                  onClick: handleResetDashboard,
                  disabled: resetting || !resetPeriodStart,
                  className:
                    'flex-1 bg-orange-600 text-white py-2 text-xs uppercase tracking-widest hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1033 },
                },
                resetting
                  ? React.createElement(Loader, {
                      size: 14,
                      className: 'animate-spin',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1034 },
                    })
                  : React.createElement(RotateCcw, {
                      size: 14,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1034 },
                    }),
                resetting ? 'Resetting...' : 'Confirm Reset'
              )
            )
          )
        )
      ),

    /* History Modal */
    showHistoryModal &&
      React.createElement(
        'div',
        {
          className: 'fixed inset-0 z-[300] flex items-center justify-center',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1045 },
        },
        React.createElement('div', {
          className: 'absolute inset-0 bg-stone-900/60 backdrop-blur-sm',
          onClick: () => setShowHistoryModal(false),
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 1046 },
        }),
        React.createElement(
          'div',
          {
            className:
              'relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-sm shadow-2xl mx-4 flex flex-col',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 1047 },
          },
          React.createElement(
            'div',
            {
              className: 'bg-stone-900 text-white p-6 flex justify-between items-center',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1048 },
            },
            React.createElement(
              'div',
              { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1049 } },
              React.createElement(
                'h3',
                {
                  className: 'font-serif text-xl',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1050 },
                },
                'Dashboard History'
              ),
              React.createElement(
                'p',
                {
                  className: 'text-xs text-stone-400 mt-1',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1051 },
                },
                'View historical snapshots and reset records'
              )
            ),
            React.createElement(
              'button',
              {
                onClick: () => setShowHistoryModal(false),
                className: 'text-stone-400 hover:text-white',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1053 },
              },
              React.createElement(X, {
                size: 20,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1054 },
              })
            )
          ),

          React.createElement(
            'div',
            {
              className: 'p-4 border-b border-stone-200 bg-stone-50 flex gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1058 },
            },
            React.createElement(
              'button',
              {
                onClick: () => {
                  setHistoryPeriod('all');
                  fetchHistory();
                },
                className: `px-3 py-1 text-xs rounded-full ${historyPeriod === 'all' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1059 },
              },
              'All'
            ),
            React.createElement(
              'button',
              {
                onClick: () => {
                  setHistoryPeriod('daily');
                  fetchHistory();
                },
                className: `px-3 py-1 text-xs rounded-full ${historyPeriod === 'daily' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1060 },
              },
              'Daily'
            ),
            React.createElement(
              'button',
              {
                onClick: () => {
                  setHistoryPeriod('weekly');
                  fetchHistory();
                },
                className: `px-3 py-1 text-xs rounded-full ${historyPeriod === 'weekly' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1061 },
              },
              'Weekly'
            ),
            React.createElement(
              'button',
              {
                onClick: () => {
                  setHistoryPeriod('monthly');
                  fetchHistory();
                },
                className: `px-3 py-1 text-xs rounded-full ${historyPeriod === 'monthly' ? 'bg-gold-600 text-white' : 'bg-stone-200 text-stone-600'}`,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1062 },
              },
              'Monthly'
            ),
            React.createElement(
              'button',
              {
                onClick: fetchHistory,
                className:
                  'ml-auto px-3 py-1 text-xs bg-stone-200 text-stone-600 rounded-full hover:bg-stone-300 flex items-center gap-1',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1063 },
              },
              React.createElement(RefreshCw, {
                size: 12,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1064 },
              }),
              ' Refresh'
            )
          ),

          React.createElement(
            'div',
            {
              className: 'flex-1 overflow-y-auto p-6',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1068 },
            },
            historyLoading
              ? React.createElement(
                  'div',
                  {
                    className: 'flex justify-center py-12',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1070 },
                  },
                  React.createElement(Loader, {
                    size: 32,
                    className: 'animate-spin text-gold-600',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1071 },
                  })
                )
              : React.createElement(
                  'div',
                  {
                    className: 'space-y-8',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1074 },
                  },
                  statsHistory.length > 0 &&
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1076 } },
                      React.createElement(
                        'h4',
                        {
                          className:
                            'font-serif text-lg text-stone-900 mb-4 flex items-center gap-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1077 },
                        },
                        React.createElement(Calendar, {
                          size: 18,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1078 },
                        }),
                        ' Historical Snapshots'
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'overflow-x-auto',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1080 },
                        },
                        React.createElement(
                          'table',
                          {
                            className: 'w-full text-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1081 },
                          },
                          React.createElement(
                            'thead',
                            {
                              className: 'bg-stone-100',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1082 },
                            },
                            React.createElement(
                              'tr',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1083 },
                              },
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-left',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1084 },
                                },
                                'Date'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-left',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1085 },
                                },
                                'Period'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-right',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1086 },
                                },
                                'Revenue'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-right',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1087 },
                                },
                                'Orders'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-right',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1088 },
                                },
                                'Products'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-right',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1089 },
                                },
                                'Members'
                              )
                            )
                          ),
                          React.createElement(
                            'tbody',
                            {
                              className: 'divide-y divide-stone-100',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1092 },
                            },
                            statsHistory.map((stat) =>
                              React.createElement(
                                'tr',
                                {
                                  key: stat.id,
                                  className: 'hover:bg-stone-50',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1094 },
                                },
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1095 },
                                  },
                                  stat.stat_date
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 capitalize',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1096 },
                                  },
                                  stat.period_type
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 text-right',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1097 },
                                  },
                                  formatCurrency(stat.total_revenue)
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 text-right',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1098 },
                                  },
                                  stat.total_orders
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 text-right',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1099 },
                                  },
                                  stat.total_products
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 text-right',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1100 },
                                  },
                                  stat.total_members
                                )
                              )
                            )
                          )
                        )
                      )
                    ),

                  resetHistory.length > 0 &&
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1110 } },
                      React.createElement(
                        'h4',
                        {
                          className:
                            'font-serif text-lg text-stone-900 mb-4 flex items-center gap-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1111 },
                        },
                        React.createElement(RotateCcw, {
                          size: 18,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1112 },
                        }),
                        ' Reset History'
                      ),
                      React.createElement(
                        'div',
                        {
                          className: 'overflow-x-auto',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1114 },
                        },
                        React.createElement(
                          'table',
                          {
                            className: 'w-full text-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1115 },
                          },
                          React.createElement(
                            'thead',
                            {
                              className: 'bg-stone-100',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1116 },
                            },
                            React.createElement(
                              'tr',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1117 },
                              },
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-left',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1118 },
                                },
                                'Reset Date'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-left',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1119 },
                                },
                                'Reset By'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-left',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1120 },
                                },
                                'Period'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-right',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1121 },
                                },
                                'Revenue at Reset'
                              ),
                              React.createElement(
                                'th',
                                {
                                  className: 'px-4 py-2 text-right',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1122 },
                                },
                                'Orders at Reset'
                              )
                            )
                          ),
                          React.createElement(
                            'tbody',
                            {
                              className: 'divide-y divide-stone-100',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1125 },
                            },
                            resetHistory.map((reset) =>
                              React.createElement(
                                'tr',
                                {
                                  key: reset.id,
                                  className: 'hover:bg-stone-50',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1127 },
                                },
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1128 },
                                  },
                                  new Date(reset.reset_date).toLocaleDateString()
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1129 },
                                  },
                                  reset.reset_by
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1130 },
                                  },
                                  reset.period_start,
                                  ' - ',
                                  reset.period_end
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 text-right',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1131 },
                                  },
                                  formatCurrency(
                                    _optionalChain([
                                      reset,
                                      'access',
                                      (_34) => _34.snapshot,
                                      'optionalAccess',
                                      (_35) => _35.current_stats,
                                      'optionalAccess',
                                      (_36) => _36.total_revenue,
                                    ]) || 0
                                  )
                                ),
                                React.createElement(
                                  'td',
                                  {
                                    className: 'px-4 py-3 text-right',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1132 },
                                  },
                                  _optionalChain([
                                    reset,
                                    'access',
                                    (_37) => _37.snapshot,
                                    'optionalAccess',
                                    (_38) => _38.current_stats,
                                    'optionalAccess',
                                    (_39) => _39.total_orders,
                                  ]) || 0
                                )
                              )
                            )
                          )
                        )
                      )
                    ),

                  statsHistory.length === 0 &&
                    resetHistory.length === 0 &&
                    React.createElement(
                      'div',
                      {
                        className: 'text-center py-12 text-stone-400',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1142 },
                      },
                      React.createElement(History, {
                        size: 48,
                        className: 'mx-auto mb-4 opacity-50',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1143 },
                      }),
                      React.createElement(
                        'p',
                        { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1144 } },
                        'No history data available yet.'
                      ),
                      React.createElement(
                        'p',
                        {
                          className: 'text-xs mt-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1145 },
                        },
                        'Data will appear here after dashboard resets or daily captures.'
                      )
                    )
                )
          )
        )
      )
  );
};

export default AdminDashboard;
